import { useCallback } from 'react';
import { useContextSelector } from 'use-context-selector';
import { ApiObject, ApiOptions } from '@rudderstack/analytics-js';
import { sha256 } from 'js-sha256';
import { AnalyticsContext } from '../../contexts/Analytics.context';
import { analytics } from '../../constants/analytics.const';

interface UseAnalyticsIdentifyParams {
  id: string;
  traits?: ApiObject;
  properties?: ApiObject;
}

export interface UseAnalyticsPageParams {
  category: string;
  name: string;
  properties?: ApiObject;
}

interface UseAnalyticsTrackParams {
  id: string;
  options?: ApiOptions;
  properties?: ApiObject;
}

interface UseAnalyticsTrackClickParams {
  source: string;
  element: string;
  options?: ApiOptions;
  properties?: ApiObject;
}

interface UseAnalyticsTrackCompleteRegistrationParams {
  email: string;
  registrationMethod: 'google' | 'apple' | 'facebook' | 'email';
}

interface UseAnalyticsGroupParams {
  groupId: string;
  traits?: ApiObject;
  options?: ApiOptions;
}

export const useAnalytics = () => {
  const config = useContextSelector(AnalyticsContext, (v) => v);

  const identify = useCallback(
    async ({
      id,
      traits,
      properties = {},
    }: UseAnalyticsIdentifyParams): Promise<void> => {
      return new Promise((resolve) => {
        analytics.identify(
          id,
          traits,
          {
            version: config.version,
            environment: config.environment,
            ...properties,
          },
          () => {
            resolve();
          }
        );
      });
    },
    [config]
  );

  const page = useCallback(
    async ({
      category,
      name,
      properties = {},
    }: UseAnalyticsPageParams): Promise<void> => {
      return new Promise((resolve) => {
        analytics.page(
          category,
          name,
          {
            version: config.version,
            environment: config.environment,
            ...properties,
          },
          () => {
            resolve();
          }
        );
      });
    },
    [config]
  );

  const track = useCallback(
    async ({
      id,
      properties = {},
      options = {},
    }: UseAnalyticsTrackParams): Promise<void> => {
      return new Promise((resolve) => {
        analytics.track(
          id,
          {
            version: config.version,
            environment: config.environment,
            ...properties,
          },
          options,
          () => {
            resolve();
          }
        );
      });
    },
    [config]
  );

  const trackClick = useCallback(
    async ({
      source,
      element,
      properties = {},
      options = {},
    }: UseAnalyticsTrackClickParams): Promise<void> => {
      return new Promise((resolve) => {
        analytics.track(
          `${source} Screen: ${element} Click`,
          {
            version: config.version,
            environment: config.environment,
            ...properties,
          },
          options,
          () => {
            resolve();
          }
        );
      });
    },
    [config]
  );

  const trackCompleteRegistration = useCallback(
    async ({
      email,
      registrationMethod,
    }: UseAnalyticsTrackCompleteRegistrationParams): Promise<void> => {
      if (!config?.facebook) return;

      const queryParams = new URLSearchParams({
        event: 'CUSTOM_APP_EVENTS',
        'ud[em]': sha256(email), // sha256 representation of email.
        custom_events: JSON.stringify([
          {
            _eventName: 'fb_mobile_complete_registration',
            fb_registration_method: registrationMethod,
          },
        ]),
      });

      const url = new URL(
        `${config.facebook.appId}/activities/?${queryParams}&${config.facebook.clientToken}`,
        `https://graph.facebook.com`
      );

      await fetch(url, { method: 'POST' });
    },
    [config]
  );

  const group = useCallback(
    async ({
      groupId,
      traits = {},
      options = {},
    }: UseAnalyticsGroupParams): Promise<void> => {
      return new Promise((resolve) => {
        analytics.group(groupId, traits, options, () => {
          resolve();
        });
      });
    },
    []
  );

  const reset = useCallback((): void => {
    return analytics.reset(true);
  }, []);

  return {
    getAnonymousId: analytics.getAnonymousId,
    identify,
    page,
    track,
    trackClick,
    trackCompleteRegistration,
    reset,
    group,
  };
};
